import React, { useRef, useState } from 'react'
import { View, Text, Pressable } from 'react-native'
import { CameraProps, CameraResult } from './Camera.types'
import styles from './Camera.styles'

export const Camera: React.FC<CameraProps> = ({
  onPhotoCaptured,
  onError,
  onCancel,
  children
}) => {
  const [isCapturing, setIsCapturing] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handlePress = () => {
    // Sur web, on peut soit utiliser l'input file avec capture, soit getUserMedia
    // On va d'abord essayer getUserMedia pour une vraie interface de caméra
    startCamera()
  }

  const startCamera = async () => {
    try {
      setIsCapturing(true)
      
      // Demander l'accès à la caméra avec préférences optimisées
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user', // Caméra frontale par défaut
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          aspectRatio: { ideal: 16/9 }
        },
        audio: false
      })
      
      setStream(mediaStream)
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
        
        // Attendre que la vidéo soit prête avant de jouer
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play()
        }
      }
    } catch (error) {
      console.error('Erreur accès caméra:', error)
      setIsCapturing(false)
      
      // Message d'erreur plus informatif
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue'
      if (errorMessage.includes('Permission denied')) {
        onError?.(new Error('Accès à la caméra refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.'))
      } else {
        // Fallback: utiliser l'input file avec capture
        if (inputRef.current) {
          inputRef.current.click()
        }
      }
    }
  }

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    setIsProcessing(true)
    
    const video = videoRef.current
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    if (!context) {
      setIsProcessing(false)
      return
    }

    // Effet flash visuel
    if (videoRef.current) {
      videoRef.current.style.filter = 'brightness(1.5) contrast(1.2)'
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.style.filter = 'brightness(1.05) contrast(1.1)'
        }
      }, 200)
    }

    // Définir la taille du canvas
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Dessiner la frame courante sur le canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Convertir en blob puis en data URL
    canvas.toBlob((blob) => {
      if (blob) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const result = e.target?.result as string
          const photoResult: CameraResult = {
            uri: result,
            name: `photo_${new Date().getTime()}.jpg`,
            type: 'image/jpeg',
            size: blob.size
          }
          
          setIsProcessing(false)
          stopCamera()
          onPhotoCaptured(photoResult)
        }
        reader.readAsDataURL(blob)
      } else {
        setIsProcessing(false)
      }
    }, 'image/jpeg', 0.9)
  }

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
    setIsCapturing(false)
  }

  const handleCancel = () => {
    stopCamera()
    onCancel?.()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    
    if (!files || files.length === 0) {
      onCancel?.()
      return
    }

    const file = files[0]
    const reader = new FileReader()
    
    reader.onload = (e) => {
      const result = e.target?.result
      if (result) {
        const photoResult: CameraResult = {
          uri: result as string,
          name: file.name,
          type: file.type,
          size: file.size
        }
        onPhotoCaptured(photoResult)
      }
    }
    
    reader.onerror = () => {
      onError?.(new Error(`Erreur lors de la lecture du fichier: ${file.name}`))
    }
    
    reader.readAsDataURL(file)

    // Réinitialiser l'input
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  return (
    <>
      {/* Input caché pour fallback */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        style={styles.hiddenInput as any}
      />
      
      {/* Interface caméra moderne */}
      {isCapturing && (
        <div style={styles.videoContainer as any}>

   
          {/* Vidéo avec style moderne */}
          <video
            ref={videoRef}
            style={{
              ...(styles.video as any),
              objectFit: 'cover',
              filter: 'brightness(1.05) contrast(1.1)',
            } as React.CSSProperties}
            autoPlay
            playsInline
            muted            
          />
          
          {/* Canvas caché pour capture */}
          <canvas
            ref={canvasRef}
            style={styles.hiddenCanvas as any}
          />
          
          {/* Contrôles modernes */}
          <View style={styles.controls}>
            <Pressable 
              style={[styles.button, styles.cancelButton]} 
              onPress={handleCancel}
            >
              <Text style={styles.buttonText}>Annuler</Text>
            </Pressable>
            
            <Pressable 
              style={[
                styles.button,
                 styles.validationButton
              ]} 
              onPress={capturePhoto}
              disabled={isProcessing}
            >
                <Text style={styles.buttonText}>Capturer</Text>
            </Pressable>
        
          </View>
        </div>
      )}
      
      {/* Bouton déclencheur */}
      <Pressable onPress={handlePress}>
        {children}
      </Pressable>
    </>
  )
}

export default Camera